'use client';

import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

const demoScenes = [
  {
    title: 'Daily Aura Brief',
    eyebrow: 'Morning signal',
    icon: 'SunIcon',
    accent: 'from-[#f6d77a] to-[#b8860b]',
    panelGlow: 'rgba(212,175,55,0.18)',
    note: 'Your profile, timing, and saved reflections become one clear daily brief.',
    insight: 'Best window: 9:30 AM - 1:00 PM',
    tags: ['Focus', 'Timing', 'Reflection'],
  },
  {
    title: 'Planet Impact',
    eyebrow: 'Live pattern',
    icon: 'SparklesIcon',
    accent: 'from-[#9fb7ff] to-[#6950a8]',
    panelGlow: 'rgba(120,116,255,0.16)',
    note: 'Transits, numerology, and mood check-ins are translated into practical prompts.',
    insight: 'Mercury favors written decisions',
    tags: ['Charts', 'Cycles', 'Guidance'],
  },
  {
    title: 'Saved Reading',
    eyebrow: 'Private history',
    icon: 'BookmarkSquareIcon',
    accent: 'from-[#79e2bd] to-[#2d8f72]',
    panelGlow: 'rgba(72,187,137,0.15)',
    note: 'Save what resonated, compare it later, and build a personal pattern archive.',
    insight: '3 recurring themes this month',
    tags: ['History', 'Favorites', 'Memory'],
  },
];

const signalOrbit = ['Chart', 'Tarot', 'Palm', 'Dream'];

export default function ProductMotionDemo() {
  const reduceMotion = useReducedMotion();
  const [activeScene, setActiveScene] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const scene = demoScenes[activeScene];
  const progress = useMemo(
    () => ((activeScene + 1) / demoScenes.length) * 100,
    [activeScene]
  );

  useEffect(() => {
    if (reduceMotion || !isPlaying) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveScene((current) => (current + 1) % demoScenes.length);
    }, 3400);

    return () => window.clearInterval(timer);
  }, [isPlaying, reduceMotion]);

  return (
    <section
      id="product-demo"
      className="product-motion-demo relative overflow-hidden py-20 md:py-24"
      aria-labelledby="product-demo-title"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(212,175,55,0.12),transparent_28%),radial-gradient(circle_at_85%_70%,rgba(99,102,241,0.10),transparent_30%)] pointer-events-none" />

      <div className="container relative z-10 mx-auto px-5">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div className="space-y-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent/24 bg-[#171117]/84 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-accent shadow-[0_14px_40px_rgba(0,0,0,0.24)]">
              <Icon name="PlayCircleIcon" size={17} variant="solid" />
              Interactive Demo
            </div>

            <div className="space-y-4">
              <h2
                id="product-demo-title"
                className="font-playfair text-4xl font-bold tracking-wide text-foreground md:text-5xl"
              >
                A living preview, not a static promise
              </h2>
              <p className="max-w-xl text-lg leading-8 text-[#ead8b7]/72">
                Move through daily signals, guided interpretation, and saved personal history in one focused flow.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {demoScenes.map((item, index) => {
                const selected = activeScene === index;

                return (
                  <button
                    key={item.title}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      setActiveScene(index);
                      setIsPlaying(false);
                    }}
                    className={`min-h-16 rounded-2xl border px-4 py-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#120d12] ${selected
                      ? 'border-accent/45 bg-accent/14 text-[#fff6df] shadow-[0_18px_42px_rgba(212,175,55,0.16)]'
                      : 'border-[#e8d7b0]/12 bg-[#171117]/72 text-[#ead8b7]/68 hover:border-accent/30 hover:text-[#fff6df]'
                      }`}
                  >
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
                      {item.eyebrow}
                    </span>
                    <span className="mt-1 block text-sm font-semibold">{item.title}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setIsPlaying((current) => !current)}
              className="inline-flex min-h-12 items-center gap-3 rounded-full border border-accent/30 bg-[#d4af37] px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#211606] shadow-[0_18px_48px_rgba(212,175,55,0.22)] transition-all hover:bg-[#f0d178] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#120d12]"
            >
              <Icon name={isPlaying ? 'PauseIcon' : 'PlayIcon'} size={18} variant="solid" />
              {isPlaying ? 'Pause demo' : 'Play demo'}
            </button>
          </div>

          <div className="card-surface manuscript-frame relative overflow-hidden rounded-xl border border-[#e8d7b0]/12 p-4 shadow-card-depth md:p-6">
            <div
              className="absolute inset-x-10 top-8 h-36 rounded-full blur-3xl transition-colors duration-700"
              style={{ backgroundColor: scene.panelGlow }}
            />

            <div className="relative overflow-hidden rounded-xl border border-[#e8d7b0]/12 bg-[#0f0b10]/86">
              <div className="flex items-center justify-between border-b border-[#e8d7b0]/10 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#ff6b6b]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#f6c35f]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#64d49c]" />
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#ead8b7]/56">
                  youraura.life
                </div>
              </div>

              <div className="grid gap-0 lg:grid-cols-[0.88fr_1.12fr]">
                <div className="relative min-h-[430px] overflow-hidden border-b border-[#e8d7b0]/10 bg-[#100b12] p-5 lg:border-b-0 lg:border-r">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(212,175,55,0.12),transparent_32%)]" />
                  <div className="relative mx-auto flex h-[390px] max-w-[230px] flex-col overflow-hidden rounded-[2rem] border border-[#e8d7b0]/16 bg-[#171017] p-3 shadow-[0_24px_70px_rgba(0,0,0,0.48)]">
                    <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-[#ead8b7]/16" />

                    <div className="relative flex-1 overflow-hidden rounded-[1.55rem] border border-[#e8d7b0]/10 bg-[#0b0810] p-4">
                      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(212,175,55,0.10),transparent_42%,rgba(111,92,190,0.12))]" />

                      <motion.div
                        className="relative mx-auto mt-5 flex h-32 w-32 items-center justify-center rounded-full border border-accent/24 bg-accent/8"
                        animate={reduceMotion ? undefined : { rotate: 360 }}
                        transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
                      >
                        {signalOrbit.map((signal, index) => (
                          <motion.span
                            key={signal}
                            className="absolute rounded-full border border-[#e8d7b0]/14 bg-[#171117] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-[#f7ead2]"
                            style={{
                              transform: `rotate(${index * 90}deg) translateY(-74px) rotate(-${index * 90}deg)`,
                            }}
                            animate={reduceMotion ? undefined : { opacity: [0.55, 1, 0.55] }}
                            transition={{ duration: 2.6, repeat: Infinity, delay: index * 0.35 }}
                          >
                            {signal}
                          </motion.span>
                        ))}
                        <motion.div
                          key={scene.title}
                          initial={reduceMotion ? false : { scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.35 }}
                          className={`flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br ${scene.accent} text-[#160f08] shadow-[0_14px_32px_rgba(0,0,0,0.32)]`}
                        >
                          <Icon name={scene.icon} size={30} variant="solid" />
                        </motion.div>
                      </motion.div>

                      <motion.div
                        key={`${scene.title}-brief`}
                        initial={reduceMotion ? false : { y: 16, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.36, ease: 'easeOut' }}
                        className="relative mt-8 space-y-3"
                      >
                        <div className="rounded-2xl border border-[#e8d7b0]/12 bg-white/[0.05] p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">
                            {scene.eyebrow}
                          </p>
                          <p className="mt-1 text-sm font-semibold leading-5 text-[#fff6df]">
                            {scene.insight}
                          </p>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {scene.tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full border border-[#e8d7b0]/10 bg-black/20 px-1.5 py-1 text-center text-[8px] font-semibold text-[#ead8b7]/78"
                            >
                              {tag === 'Reflection' ? 'Reflect' : tag}
                            </span>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>

                <div className="relative flex min-h-[430px] flex-col justify-between p-5 md:p-7">
                  <div className="space-y-5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 rounded-full border border-[#e8d7b0]/12 bg-white/[0.05] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#ead8b7]/70">
                        <Icon name="VideoCameraIcon" size={15} />
                        Demo Reel
                      </div>
                      <span className="rounded-full bg-[#f4d47c] px-3 py-1 text-xs font-bold text-[#22170a]">
                        0{activeScene + 1}/0{demoScenes.length}
                      </span>
                    </div>

                    <motion.div
                      key={`${scene.title}-copy`}
                      initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.38 }}
                      className="space-y-4"
                    >
                      <h3 className="font-playfair text-3xl font-bold tracking-wide text-[#fff6df] md:text-4xl">
                        {scene.title}
                      </h3>
                      <p className="max-w-md text-base leading-7 text-[#ead8b7]/70">
                        {scene.note}
                      </p>
                    </motion.div>

                    <div className="grid gap-3">
                      {scene.tags.map((tag, index) => (
                        <motion.div
                          key={`${scene.title}-${tag}`}
                          initial={reduceMotion ? false : { opacity: 0, x: 16 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.08 }}
                          className="flex items-center gap-3 rounded-2xl border border-[#e8d7b0]/10 bg-[#171117]/78 px-4 py-3"
                        >
                          <span className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${scene.accent} text-[#160f08]`}>
                            <Icon name="CheckIcon" size={18} strokeWidth={3} />
                          </span>
                          <span className="text-sm font-semibold text-[#f7ead2]">
                            {tag} layer synced
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 space-y-3">
                    <div className="h-2 overflow-hidden rounded-full bg-[#ead8b7]/10">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-[#d4af37] via-[#f5d57b] to-[#7ddfbd]"
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.35, ease: 'easeOut' }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-[#ead8b7]/54">
                      <span>Preview loop</span>
                      <span>{isPlaying && !reduceMotion ? 'Playing' : 'Paused'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
